import Interactable from '../../../Component/Interactable';
import Renderable from '../../../Component/Renderable';
import UITextComponent from '../../../Component/UI/Text';
import Game from '../../../Game';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { IGameState } from '../../../State/Game/IGameState';
import { PlayerActions } from '../../../State/Player/PlayerActions';
import { PlayerTask } from '../../../State/Player/PlayerTasks';
import { UIActions } from '../../../State/UI/UIActions';
import DateHelper from '../../../Utility/DateHelper';
import { AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import BaseUISystem from '../BaseUISystem';
import BaseUIHandler from '../BaseUIHandler';
import {
    applyBounceButtonAnimation,
    applyDefaultButtonAnimation,
} from '../UIHelper';

// top
const LIVES = 'top_container/world_hud_lives_panel';
const COINS = 'top_container/world_hud_coins_panel';
const STARS = 'top_container/world_hud_stars_panel';

const TEXT_LIVES_TIMER = `${LIVES}/world_hud_lives_timer`;
const TEXT_LIVES = `${LIVES}/world_hud_lives_value`;
const BTN_PLUS_LIVE = `${LIVES}/plus_button`;

const TEXT_COINS = `${COINS}/world_hud_coins_value`;
const BTN_PLUS_SHOP = `${COINS}/plus_button`;

const TEXT_STARS = `${STARS}/world_hud_stars_value`;

const BTN_WHEEL = 'top_container/world_hud_wheel';
const TEXT_WHEEL = `${BTN_WHEEL}/wheel_text`;
const ICON_WHEEL_AD = `${BTN_WHEEL}/ad_icon`;
const NOTIFICATION_WHEEL = `${BTN_WHEEL}/notification`;

// bottom
const BTN_SETTINGS = 'bottom_container/settings_button';
const BTN_TRAVEL = 'bottom_container/travel_button';

const THIRTY_MINUTES = DateHelper.minutesToMilliseconds(30);
const TWELVE_HOURS = DateHelper.hoursToMilliseconds(12);

export default class WorldHUDHandler extends BaseUIHandler {
    private lifeTimerInterval: any;
    private wheelTimerInterval: any;

    constructor(uiSystem: BaseUISystem) {
        super('worldHUD', uiSystem);
        this.alwaysVisible = true;
    }

    public handleStateChanged(state: IGameState): void {
        this.setLifeTimerInterval();
        this.setWheelTimerInterval();

        this.updateWheelText();
    }

    protected onCreate() {
        const textCoins = this.element.find(TEXT_COINS);
        const textStars = this.element.find(TEXT_STARS);

        const textLivesValue = this.element.find(TEXT_LIVES);
        textLivesValue.getComponent(
            UITextComponent
        ).text = Game.state.getState().playerState.lives.toString();

        // This is a somewhat hacky solution to set the initial coin value in the world HUD when
        // returning from a level. If the player completes the level and earns coins a "EARN_COINS" task
        // will be queued. Therefore the initial value of coins in the HUD should be the "previousCoinsValue"
        // property because it will animate the value increasing. However, if the player had earned coins
        // during the level (from IAP for example) and quit the level the coins HUD must show the current
        // value - no task will be queued.

        let coins = Game.state.getState().playerState.coins;
        if (
            Game.state
                .getState()
                .playerState.tasks.find(t => t.task === PlayerTask.EARN_COINS)
        ) {
            coins = Game.state.getState().playerState.previousCoinsValue;
        }

        textCoins.getComponent(UITextComponent).text = coins.toString();

        textStars.getComponent(
            UITextComponent
        ).text = Game.player.getTotalStars().toString();

        this.updateLivesText();
        this.setLifeTimerInterval();

        this.updateWheelText();
        this.setWheelTimerInterval();

        // Button wheel.
        const btnWheel = this.element.find(BTN_WHEEL);
        btnWheel.on(GameObjectEvent.SELECTED, () => {
            if (
                this.system.state.getState().playerState.wheelSpins > 0 ||
                Game.state.getState().platform.adsRewarded
            ) {
                this.system.state.dispatch(UIActions.showPopup('wheelMenu'));
            }
        });
        applyBounceButtonAnimation(
            btnWheel,
            AUDIO_UI_BUTTON_DEFAULT,
            'SpinWheelPulse'
        );

        // Button plus (live).
        const btnPlusLive = this.element.find(BTN_PLUS_LIVE);
        btnPlusLive.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.showPopup('noLivesMenu'));
        });
        applyDefaultButtonAnimation(btnPlusLive, AUDIO_UI_BUTTON_DEFAULT);

        // Button plus (shop).
        const btnPlusShop = this.element.find(BTN_PLUS_SHOP);
        btnPlusShop.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.showPopup('shopMenu'));
        });
        applyDefaultButtonAnimation(btnPlusShop, AUDIO_UI_BUTTON_DEFAULT);
        btnPlusShop.getComponent(
            Renderable
        ).visible = Game.state.getState().platform.IAPSupport;

        // Button settings.
        const btnSettings = this.element.find(BTN_SETTINGS);
        btnSettings.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                UIActions.showPopup('settingsMenu', {
                    enableBtnQuit: false,
                })
            );
        });
        applyDefaultButtonAnimation(btnSettings, AUDIO_UI_BUTTON_DEFAULT);

        // Button travel.
        const btnTravel = this.element.find(BTN_TRAVEL);
        btnTravel.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.showPopup('travelMenu'));
        });
        applyDefaultButtonAnimation(btnTravel, AUDIO_UI_BUTTON_DEFAULT);
    }

    protected onDestroy(): void {
        Game.ticker.clearInterval(this.lifeTimerInterval);
        this.lifeTimerInterval = null;
        Game.ticker.clearInterval(this.wheelTimerInterval);
        this.wheelTimerInterval = null;
    }

    private onLifeTimer() {
        const currentLifeTimerDate = Game.state.getState().playerState
            .lifeTimer;
        if (currentLifeTimerDate === null) {
            this.updateLivesText(); // Set text timer to MAX.
            Game.ticker.clearInterval(this.lifeTimerInterval);
            this.lifeTimerInterval = null;
            return;
        }

        // Check if thirty minutes has passed since player's life timer started.
        // If it has trigger an update to player's timer state.

        const now = DateHelper.now();
        const timeDiff =
            now.getTime() -
            DateHelper.getDateFromValue(currentLifeTimerDate).getTime();

        if (timeDiff >= THIRTY_MINUTES) {
            Game.state.dispatch(PlayerActions.updateTimers());
        }

        this.updateLivesText();
    }

    private setLifeTimerInterval() {
        if (Game.state.getState().playerState.lifeTimer === null) return;
        if (typeof this.lifeTimerInterval === 'function') return;

        this.lifeTimerInterval = Game.ticker.setInterval(
            this.onLifeTimer.bind(this),
            1000
        );
    }

    private updateLivesText() {
        // Check that element exists - this handles a rare case where
        // updateLivesText is called after destroy() despite intervals being "cleared".
        if (!this.element || this.element.destroyed) return;

        const lifeTimerDate = Game.state.getState().playerState.lifeTimer;

        const textLivesTimer = this.element.find(TEXT_LIVES_TIMER);
        const textLivesComponent = textLivesTimer.getComponent(UITextComponent);
        const btnPlusLive = this.element.find(BTN_PLUS_LIVE);
        const btnPlusLiveRenderable = btnPlusLive.getComponent(Renderable);

        // Max lives.
        if (lifeTimerDate === null) {
            textLivesComponent.text = Game.locale.getText('Max');
            Game.ticker.clearInterval(this.lifeTimerInterval);
            this.lifeTimerInterval = null;
            btnPlusLiveRenderable.visible = false;
            return;
        }

        textLivesComponent.text = DateHelper.timeTo(
            DateHelper.now(),
            DateHelper.getDateFromValue(
                DateHelper.getDateFromValue(lifeTimerDate).getTime() +
                    THIRTY_MINUTES
            )
        );
        btnPlusLiveRenderable.visible = true;
    }

    private onWheelTimer() {
        const currentWheelTimerDate = Game.state.getState().playerState
            .wheelTimer;
        if (currentWheelTimerDate === null) {
            this.updateWheelText(); // Set text timer to FreeSpin.
            Game.ticker.clearInterval(this.wheelTimerInterval);
            this.wheelTimerInterval = null;
            return;
        }

        // Check if twelve hours has passed since player's spins timer started.
        // If it has trigger an update to player's timer state.

        const now = DateHelper.now();
        const timeDiff =
            now.getTime() -
            DateHelper.getDateFromValue(currentWheelTimerDate).getTime();

        if (timeDiff >= TWELVE_HOURS) {
            Game.state.dispatch(PlayerActions.updateTimers());
        }

        this.updateWheelText();
    }

    private setWheelTimerInterval() {
        if (Game.state.getState().playerState.wheelTimer === null) {
            this.updateWheelText();
            return;
        }

        if (typeof this.wheelTimerInterval === 'function') return;

        this.wheelTimerInterval = Game.ticker.setInterval(
            this.onWheelTimer.bind(this),
            1000
        );
    }

    private updateWheelText() {
        // Check that element exists - this handles a rare case where
        // updateWheelText is called after destroy() despite intervals being "cleared".
        if (!this.element || this.element.destroyed) return;

        // If there are spins available enable button and update "notification",
        // otherwise disable button and hide notification.
        const spins = Game.state.getState().playerState.wheelSpins;
        const btnWheel = this.element.find(BTN_WHEEL);
        const notification = this.element.find(NOTIFICATION_WHEEL);
        const iconWheelAD = this.element.find(ICON_WHEEL_AD);
        if (spins > 0) {
            btnWheel.getComponent(Interactable).enabled = true;
            notification.getComponent(Renderable).visible = true;
            notification
                .find('text')
                .getComponent(UITextComponent).text = spins.toString();
            iconWheelAD.getComponent(Renderable).visible = false;
        } else if (Game.state.getState().platform.adsRewarded) {
            btnWheel.getComponent(Interactable).enabled = true;
            notification.getComponent(Renderable).visible = false;
            iconWheelAD.getComponent(Renderable).visible = true;
        } else {
            btnWheel.getComponent(Interactable).enabled = false;
            notification.getComponent(Renderable).visible = false;
            iconWheelAD.getComponent(Renderable).visible = false;
        }

        // Update timer or if player has at least 1 spin, set text as "Free Spin".
        const wheelTimeDate = Game.state.getState().playerState.wheelTimer;

        const textWheel = this.element.find(TEXT_WHEEL);
        const textWheelComponent = textWheel.getComponent(UITextComponent);

        if (wheelTimeDate === null) {
            textWheel.getComponent(Renderable).visible = true;
            textWheelComponent.text = Game.locale.getText('FreeSpin');
            Game.ticker.clearInterval(this.wheelTimerInterval);
            this.wheelTimerInterval = null;
        } else {
            textWheel.getComponent(Renderable).visible = false;
        }
    }
}
